import { Component, DestroyRef, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MessageService } from '../../../../services/message/message.service';
import { DeliveryService, DeliverySummary, receiptsStillDue } from '../../../../services/message/delivery.service';
import { DropdownComponent } from '../../../../components/ui/dropdown/dropdown.component';
import { StatusComponent } from '../../../../components/ui/status/status.component';
import { PromptCardComponent } from '../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { SmsBubbleComponent } from '../../../../components/messages/sms-bubble/sms-bubble.component';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { RecipientList } from './recipient-list/recipient-list';
import { DeliverySummaryComponent } from '../message-reports/delivery-summary/delivery-summary.component';
import { MessageDetailsRecipientList } from '../../campaigns/types/messagesTypes';
import {
  SmsParts,
  formatCount,
  formatCountdown,
  formatWhen,
  isScheduled,
  sendMoment,
  smsParts,
  toDate,
} from '../../../../../utils/sms';
import { csvFileName, downloadCsv, toCsv } from '../../../../../utils/csv';
import {
  COLLECT_CAP,
  DeliveryFilter,
  RECEIPT_CSV_COLUMNS,
  ReceiptView,
  ResendTarget,
  collectReportRows,
  deliveryCriteria,
  exactTime,
  isoTime,
  openComposerForResend,
  resendNumbers,
  resendTarget,
  toReceiptViews,
} from '../message-reports/report-shared';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

/** Cancel was refused: since the page loaded, the send fell due, went out or was cancelled elsewhere. */
class CancelTooLateError extends Error {
  constructor(readonly reason: 'due' | 'sent' | 'cancelled') {
    super('Too late to cancel');
  }
}

/** Failed recipients listed on the details page before "See all". */
const FAILED_PREVIEW = 5;

@Component({
  selector: 'message-details-view',
  standalone: true,
  imports: [
    RouterLink,
    MatTooltipModule,
    DropdownComponent,
    StatusComponent,
    PromptCardComponent,
    SmsBubbleComponent,
    HasPermissionDirective,
    RecipientList,
    DeliverySummaryComponent,
  ],
  templateUrl: './message-details-view.html',
  styleUrls: ['./message-details-view.css'],
})
export class MessageDetailsView implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private delivery = inject(DeliveryService);
  private destroyRef = inject(DestroyRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @ViewChild(DropdownComponent, { static: false })
  dropdownComponent?: DropdownComponent;

  readonly formatCount = formatCount;
  readonly collectCap = COLLECT_CAP;

  // ── The message ──
  messageUuid: string = ''; // the uuid from the route
  detail_data: any = {};
  loadState: LoadState = 'loading';
  /** engagementContactLists, handed to the recipients modal. */
  recipient: any[] = [];
  open_recipient: boolean = false;

  // ── Recipients ──
  recipientsData: MessageDetailsRecipientList | null = null;
  totalRecipients: number = 0;
  isLoadingRecipients: boolean = false;
  recipientsError: string = '';

  // ── Delivery ──
  summary: DeliverySummary | null = null;
  summaryState: LoadState = 'idle';
  failedPreview: ReceiptView[] = [];
  failedPreviewState: LoadState = 'idle';

  // ── Actions ──
  archiving = false;
  actionError = '';
  showCancelPrompt = false;
  cancelling = false;
  cancelError = '';
  resendBusy = false;
  resendError = '';
  resendPrompt: ResendTarget | null = null;
  exportBusy: 'recipients' | 'failed' | null = null;
  exportNote = '';
  exportError = '';

  private messageSub?: Subscription;
  private recipientsSub?: Subscription;
  private deliverySubs = new Subscription();
  /** Export and resend run separately, so starting one never cancels the other. */
  private exportWork?: Subscription;
  private resendWork?: Subscription;
  private cancelSub?: Subscription;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.messageSub?.unsubscribe();
      this.recipientsSub?.unsubscribe();
      this.deliverySubs.unsubscribe();
      this.exportWork?.unsubscribe();
      this.resendWork?.unsubscribe();
      this.cancelSub?.unsubscribe();
    });
  }

  // ── Derived values ──

  get getMessageDetails(): any {
    return this.detail_data;
  }

  get subject(): string {
    return String(this.detail_data?.messageSubject ?? '').trim() || 'Untitled message';
  }

  get content(): string {
    return String(this.detail_data?.content || this.detail_data?.messageTemplate || '');
  }

  get status(): string {
    return String(this.detail_data?.status ?? '');
  }

  get statusKey(): string {
    return this.status.toLowerCase();
  }

  get isSent(): boolean {
    return this.statusKey === 'completed' || this.statusKey === 'active';
  }

  get isPending(): boolean {
    return this.statusKey === 'pending';
  }

  get isCompleted(): boolean {
    return this.statusKey === 'completed';
  }

  get isArchived(): boolean {
    return !!this.detail_data?.archive;
  }

  get scheduled(): boolean {
    return isScheduled(this.detail_data);
  }

  get from(): string {
    return String(this.detail_data?.from ?? '').trim();
  }

  get moment(): Date | null {
    return sendMoment(this.detail_data);
  }

  get momentText(): string {
    const moment = this.moment;
    return moment ? formatWhen(moment) : '—';
  }

  get momentExact(): string {
    return exactTime(this.moment);
  }

  get momentIso(): string {
    return isoTime(this.moment);
  }

  /** What the date beside the message means for this status. */
  get momentLabel(): string {
    switch (this.statusKey) {
      case 'completed':
      case 'active':
        return 'Sent';
      case 'pending':
        return 'Scheduled for';
      case 'canceled':
      case 'cancelled':
        return this.scheduled ? 'Was scheduled for' : 'Created';
      default:
        return this.scheduled ? 'Scheduled for' : 'Created';
    }
  }

  get countdown(): string {
    return this.isPending ? formatCountdown(this.moment) : '';
  }

  /** A pending send can be cancelled until it falls due; after that it may be going out. */
  get canCancel(): boolean {
    return this.isPending && this.countdown !== 'due now';
  }

  get eyebrow(): string {
    if (this.statusKey === 'draft') return 'Draft message';
    if (this.isPending && this.scheduled) return 'Scheduled message';
    return 'Message';
  }

  /** Delivery card copy before a message has receipts. */
  get idleNote(): string {
    switch (this.statusKey) {
      case 'pending': {
        const when = this.moment ? ` ${this.momentText}` : '';
        const countdown = this.countdown ? `, ${this.countdown}` : '';
        return `Goes out${when}${countdown}. Delivery receipts show here once it is sent.`;
      }
      case 'draft':
        return 'This message has not been sent. Delivery shows here once it goes out.';
      case 'canceled':
      case 'cancelled':
        return 'This send was cancelled before it went out, so nothing was delivered.';
      default:
        return 'Delivery shows here once the message goes out.';
    }
  }

  get typeLabel(): string {
    return this.scheduled ? 'Scheduled' : 'Instant';
  }

  get timeZone(): string {
    return String(this.detail_data?.scheduleTimeZone ?? '').trim();
  }

  /**
   * The send time in the schedule's own zone ("Tue 13 Oct, 09:00"), when that
   * differs from the viewer's. The times above are in the viewer's zone, so
   * without this the Time zone row reads as a contradiction.
   */
  get zoneTime(): string {
    const moment = this.moment;
    const zone = this.timeZone;
    if (!moment || !zone) return '';
    try {
      if (Intl.DateTimeFormat().resolvedOptions().timeZone === zone) return '';
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: zone,
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).format(moment);
    } catch {
      return ''; // not a zone this browser knows
    }
  }

  get createdText(): string {
    const created = toDate(this.detail_data?.createdAt);
    return created ? formatWhen(created) : '';
  }

  get createdExact(): string {
    return exactTime(this.detail_data?.createdAt);
  }

  get createdIso(): string {
    return isoTime(this.detail_data?.createdAt);
  }

  /** The API sends a user ({ id, login }); older rows may hold a plain name. */
  get createdBy(): string {
    const by = this.detail_data?.createdBy;
    if (!by) return '';
    if (typeof by === 'string') return by.trim();
    const user = by as { login?: string; firstName?: string; lastName?: string };
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || String(user.login ?? '').trim();
  }

  private partsMemo: { text: string; value: SmsParts } | null = null;

  private get parts(): SmsParts {
    const text = this.content;
    let memo = this.partsMemo;
    if (!memo || memo.text !== text) {
      memo = { text, value: smsParts(text) };
      this.partsMemo = memo;
    }
    return memo.value;
  }

  get partsCount(): number {
    return this.content ? this.parts.parts : 0;
  }

  get partsText(): string {
    const parts = this.partsCount;
    if (!parts) return '—';
    return parts === 1 ? '1 part' : `${parts} parts`;
  }

  get partsEstimated(): boolean {
    return !!this.content && this.parts.estimated;
  }

  /** SMS the send costs: parts per message times recipients. */
  get smsTotalText(): string {
    if (!this.partsCount || !this.totalRecipients) return '';
    const about = this.partsEstimated ? 'About ' : '';
    return `${about}${formatCount(this.partsCount * this.totalRecipients)} SMS`;
  }

  get blacklistedCount(): number {
    const list = this.recipientsData?.blackListedUuids;
    return Array.isArray(list) ? list.length : 0;
  }

  get audience(): { label: string; count: number }[] {
    const data = this.recipientsData;
    if (!data) return [];
    const size = (list: unknown) => (Array.isArray(list) ? list.length : 0);
    return [
      { label: 'Contacts', count: size(data.contacts) },
      { label: 'Organizations', count: size(data.engagementRecipientCompanies) },
      { label: 'Tags', count: size(data.engagementRecipientTags) },
      { label: 'Segments', count: size(data.engagementRecipientSegments) },
    ].filter((group) => group.count > 0);
  }

  get failedCount(): number {
    return this.summary?.failed ?? 0;
  }

  /** "Goes out in 3 h to 1,240 recipients, 2 parts each". */
  get cancelImpact(): string {
    const total = this.totalRecipients;
    if (!this.countdown && !total) return '';
    let line = this.countdown ? `Goes out ${this.countdown}` : 'Goes out as scheduled';
    if (total) line += ` to ${formatCount(total)} ${total === 1 ? 'recipient' : 'recipients'}`;
    if (this.partsCount > 1) line += `, ${this.partsCount} parts each`;
    return line;
  }

  get resendCount(): number {
    return this.resendPrompt?.numbers.length ?? 0;
  }

  get resendViaState(): boolean {
    return !!this.resendPrompt?.viaState;
  }

  get resendCapped(): boolean {
    return !!this.resendPrompt?.capped;
  }

  get resendBlacklisted(): number {
    return this.resendPrompt?.blacklisted ?? 0;
  }

  // ── Navigation and the actions menu ──

  handleEdit() {
    this.router.navigate(['/organization/messages']);
  }

  handleRecipientClose() {
    this.open_recipient = false;
  }

  handleTabMenu(from: string) {
    if (from === 'View recipients') {
      this.open_recipient = true;
      // Close the dropdown when "View recipients" is clicked
      this.dropdownComponent?.closeOptions();
    }
    if (from === 'Duplicate message') {
      this.dropdownComponent?.closeOptions();
      // The composer reads the message to copy from the navigation state
      // (`message`), falling back to this storage key after a reload.
      this.setItem('messageDetails', this.detail_data);
      this.router.navigate(['/organization/messages/create'], {
        queryParams: { type: 'duplicate' },
        state: { message: this.detail_data },
      });
    }
    if (from === 'Archive message') {
      this.setArchived(true);
    }
    if (from === 'Unarchive message') {
      this.setArchived(false);
    }
    if (from === 'View Report') {
      this.openReport();
    }
  }

  /** The report, optionally opened on one delivery state. */
  openReport(filter?: DeliveryFilter) {
    this.dropdownComponent?.closeOptions();
    this.messageService.updateSelectedMessage(this.getMessageDetails);
    this.router.navigate(
      ['/organization/messages/report', this.messageUuid || this.getMessageDetails?.uuid],
      filter && filter !== 'all' ? { queryParams: { status: filter } } : {}
    );
  }

  private setArchived(archive: boolean) {
    if (this.archiving) return;
    this.archiving = true;
    this.actionError = '';
    this.messageService
      .updateMessageStatus({
        // The API needs the numeric id in the body and the uuid in the path.
        id: this.detail_data?.id ?? this.messageUuid,
        uuid: this.messageUuid || this.getMessageDetails?.uuid,
        status: 'Completed',
        archive,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.archiving = false;
          this.dropdownComponent?.closeOptions();
          this.router.navigate(['/organization/messages']);
        },
        error: () => {
          this.archiving = false;
          this.dropdownComponent?.closeOptions();
          this.actionError = archive
            ? 'The message could not be archived. Try again in a moment.'
            : 'The message could not be restored from the archive. Try again in a moment.';
        },
      });
  }

  // ── Cancel a scheduled send ──

  openCancelPrompt() {
    this.cancelError = '';
    this.showCancelPrompt = true;
  }

  closeCancelPrompt() {
    if (this.cancelling) return;
    this.showCancelPrompt = false;
  }

  confirmCancel() {
    const uuid = this.messageUuid || this.getMessageDetails?.uuid;
    if (this.cancelling || !uuid) return;
    this.cancelling = true;
    this.cancelError = '';

    // The API changes any status without checking it, so re-read the message
    // first: a send that has already gone out must never be relabelled Canceled.
    this.cancelSub?.unsubscribe();
    this.cancelSub = this.messageService
      .getMessage(uuid)
      .pipe(
        switchMap((fresh: any) => {
          const current = fresh && typeof fresh === 'object' ? fresh : this.detail_data;
          const status = String(current?.status ?? '');
          const at = sendMoment(current);
          const reason = /^cancel/i.test(status)
            ? 'cancelled'
            : !/^pending$/i.test(status)
            ? 'sent'
            : at && at.getTime() <= Date.now()
            ? 'due'
            : null;
          if (reason) {
            if (current !== this.detail_data) this.applyMessage(current);
            return throwError(() => new CancelTooLateError(reason));
          }
          return this.messageService.updateMessageStatus({
            id: current?.id ?? this.detail_data?.id ?? uuid,
            uuid,
            status: 'Canceled',
          });
        })
      )
      .subscribe({
        next: () => {
          this.cancelling = false;
          this.showCancelPrompt = false;
          this.detail_data = { ...this.detail_data, status: 'Canceled' };
          this.loadMessage(this.messageUuid, false);
        },
        error: (error: unknown) => {
          this.cancelling = false;
          if (error instanceof CancelTooLateError) {
            this.cancelError =
              error.reason === 'cancelled'
                ? 'This send has already been cancelled.'
                : error.reason === 'due'
                ? 'This send is due now and may already be going out, so it can no longer be cancelled. Refresh in a minute to check its status.'
                : 'This message has already started sending, so it can no longer be cancelled.';
            return;
          }
          this.cancelError =
            'The send could not be cancelled. It may already be going out: refresh the page to check its status.';
        },
      });
  }

  // ── Failed recipients: export and resend ──

  exportReceipts(kind: 'recipients' | 'failed') {
    const uuid = this.messageUuid || this.detail_data?.uuid;
    if (this.exportBusy || !uuid) return;
    this.dropdownComponent?.closeOptions();
    this.exportBusy = kind;
    this.exportError = '';
    this.exportNote = 'Preparing the file…';

    this.exportWork?.unsubscribe();
    this.exportWork = collectReportRows(
      this.messageService,
      uuid,
      kind === 'failed' ? deliveryCriteria('failed') : {},
      (loaded, total) => {
        this.exportNote = total
          ? `Preparing ${formatCount(Math.min(loaded, total))} of ${formatCount(total)} rows…`
          : `Preparing ${formatCount(loaded)} rows…`;
      }
    ).subscribe({
      next: ({ rows, total, totalKnown, capped }) => {
        this.exportBusy = null;
        if (!rows.length) {
          this.exportNote = '';
          this.exportError =
            kind === 'failed' ? 'No failed recipients to download.' : 'No delivery receipts to download yet.';
          return;
        }
        const label = kind === 'failed' ? 'failed recipients' : 'recipients';
        downloadCsv(csvFileName(label, this.subject), toCsv(rows, RECEIPT_CSV_COLUMNS));
        const of = totalKnown ? ` of ${formatCount(total)}` : '';
        this.exportNote = capped
          ? `Downloaded the first ${formatCount(rows.length)}${of} rows. Use the report's search and filters to export the rest.`
          : `Downloaded ${formatCount(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}.`;
      },
      error: () => {
        this.exportBusy = null;
        this.exportNote = '';
        this.exportError = 'The file could not be prepared. Check your connection and try again.';
      },
    });
  }

  resendToFailed() {
    const uuid = this.messageUuid || this.detail_data?.uuid;
    if (this.resendBusy || !uuid) return;
    this.resendBusy = true;
    this.resendError = '';

    this.resendWork?.unsubscribe();
    this.resendWork = collectReportRows(this.messageService, uuid, deliveryCriteria('failed')).subscribe({
      next: ({ rows, capped }) => {
        this.resendBusy = false;
        const { numbers, blacklisted } = resendNumbers(rows);
        if (!numbers.length) {
          this.resendError = blacklisted
            ? 'Every failed number is blacklisted, so there is no one to resend to.'
            : 'No failed numbers were found for this message.';
          return;
        }
        const target = resendTarget(numbers, String(this.detail_data?.messageSubject ?? ''), blacklisted, capped);
        if (target.viaState || target.capped) {
          this.resendPrompt = target;
          return;
        }
        openComposerForResend(this.router, target);
      },
      error: () => {
        this.resendBusy = false;
        this.resendError = 'The failed numbers could not be loaded. Check your connection and try again.';
      },
    });
  }

  confirmResend() {
    const target = this.resendPrompt;
    this.resendPrompt = null;
    if (target) openComposerForResend(this.router, target);
  }

  closeResendPrompt() {
    this.resendPrompt = null;
  }

  // ── Loading ──

  // Save data to localStorage
  setItem(key: string, data: any): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch {
      // Storage full or blocked: duplicating then starts from an empty form.
    }
  }

  loadMessageRecipients(messageUuid: string): void {
    this.isLoadingRecipients = true;
    this.recipientsError = '';
    // Only the latest message's answer may land, or a slow reply for the
    // previous message would show its audience here.
    this.recipientsSub?.unsubscribe();
    this.recipientsSub = this.messageService
      .fetchMessageRecipients(messageUuid)
      .subscribe({
        next: (data: MessageDetailsRecipientList) => {
          this.recipientsData = data ?? null;
          this.totalRecipients = Number(data?.totalReceipients) || 0;
          this.isLoadingRecipients = false;
        },
        error: () => {
          this.isLoadingRecipients = false;
          this.recipientsError = 'Recipients could not be loaded.';
        },
      });
  }

  /** Load the message; `fresh` shows the loading state, a refresh keeps the page. */
  loadMessage(id: string, fresh = true): void {
    if (fresh) {
      // A message handed over by the list shows at once while the request runs.
      const handed = this.messageService.getSelectedMessageDetails as any;
      if (handed?.uuid === id) {
        this.applyMessage(handed);
        this.loadState = 'ready';
      } else {
        this.detail_data = {};
        this.loadState = 'loading';
      }
    }

    this.messageSub?.unsubscribe();
    this.messageSub = this.messageService.getMessage(id).subscribe({
      next: (data: any) => {
        if (data && typeof data === 'object') {
          this.applyMessage(data);
          // Keep the shared copy current for screens that read it.
          this.messageService.detailedMessage.next(data);
        }
        this.loadState = 'ready';
      },
      error: () => {
        if (this.loadState !== 'ready') this.loadState = 'error';
      },
    });
  }

  retry() {
    if (!this.messageUuid) return;
    this.loadMessage(this.messageUuid);
    this.loadMessageRecipients(this.messageUuid);
  }

  private applyMessage(data: any) {
    const previous = this.statusKey;
    this.detail_data = data ?? {};
    if (Array.isArray(data?.engagementContactLists)) {
      this.recipient = data.engagementContactLists;
    }
    if (this.statusKey !== previous || this.summaryState === 'idle') {
      this.loadDelivery();
    }
  }

  private loadDelivery() {
    this.deliverySubs.unsubscribe();
    this.deliverySubs = new Subscription();
    this.summary = null;
    this.failedPreview = [];
    this.failedPreviewState = 'idle';

    const uuid = this.messageUuid || this.detail_data?.uuid;
    if (!this.isSent || !uuid) {
      this.summaryState = 'idle';
      return;
    }

    this.summaryState = 'loading';
    // Live while receipts are still arriving; the failed list follows the
    // failed count rather than reloading on every poll.
    let failedShown = -1;
    this.deliverySubs.add(
      this.delivery
        .live(uuid, (summary) => summary.inFlight > 0 && receiptsStillDue(this.detail_data?.status, this.moment))
        .subscribe({
          next: (summary) => {
            this.summary = summary;
            this.summaryState = 'ready';
            if (summary.failed > 0 && summary.failed !== failedShown) {
              failedShown = summary.failed;
              this.loadFailedPreview(uuid);
            }
          },
          error: () => {
            this.summaryState = 'error';
          },
        })
    );
  }

  private failedPreviewSub?: Subscription;

  /** Newest failures. A live refresh keeps the shown rows until the new ones land. */
  private loadFailedPreview(uuid: string) {
    if (this.failedPreviewState !== 'ready') this.failedPreviewState = 'loading';
    this.failedPreviewSub?.unsubscribe();
    this.failedPreviewSub = this.messageService
      .fetchMessageReportPage(uuid, {
        ...deliveryCriteria('failed'),
        page: 0,
        size: FAILED_PREVIEW,
        sort: 'id,desc',
      })
      .subscribe({
        next: ({ rows }) => {
          this.failedPreview = toReceiptViews(rows);
          this.failedPreviewState = 'ready';
        },
        error: () => {
          // Keep rows already shown; only a first load reports the error.
          if (this.failedPreviewState !== 'ready') this.failedPreviewState = 'error';
        },
      });
    this.deliverySubs.add(this.failedPreviewSub);
  }

  /** Drop everything that belonged to the previous message when the route moves to another. */
  private resetForNewMessage() {
    this.recipientsSub?.unsubscribe();
    this.exportWork?.unsubscribe();
    this.resendWork?.unsubscribe();
    this.cancelSub?.unsubscribe();
    this.deliverySubs.unsubscribe();
    this.deliverySubs = new Subscription();

    this.recipient = [];
    this.open_recipient = false;
    this.recipientsData = null;
    this.totalRecipients = 0;
    this.recipientsError = '';

    this.summary = null;
    this.summaryState = 'idle';
    this.failedPreview = [];
    this.failedPreviewState = 'idle';

    this.archiving = false;
    this.actionError = '';
    this.showCancelPrompt = false;
    this.cancelling = false;
    this.cancelError = '';
    this.resendBusy = false;
    this.resendError = '';
    this.resendPrompt = null;
    this.exportBusy = null;
    this.exportNote = '';
    this.exportError = '';
  }

  ngOnInit() {
    if (this.isBrowser) {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    }

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = params.get('id');
      if (id && id !== this.messageUuid) {
        if (this.messageUuid) this.resetForNewMessage();
        this.messageUuid = id; // Store the UUID from route params
        this.summaryState = 'idle';
        this.loadMessage(id);
        this.loadMessageRecipients(id);
      }
    });
  }
}
